import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import {
  Check, ChevronDown, Trash2, Plus, Minus, RefreshCw, ArrowLeft, Pencil, Search, Calendar, AlertTriangle, Loader2, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { getFlowPhoto, getFlowDraft, getFlowImageB64 } from './flowStore';
import FoodSearchSheet from './FoodSearchSheet';
import { findFood } from './mockFoods';
import { cleanLabel, pluralUnit, supportRecordFor } from './labels';
import { swapRowIdentity, buildAddedRow } from './rowEdits';
import { reviewCommitNote } from './reviewCopy';
import * as RR from './rebuiltReview';
import { useToday } from '../../hooks/useToday';
import useKeyboardInset from '../../hooks/useKeyboardInset';

// AI Food Logging review — photo-first confirmation flow, Fitxess styling.
// Renders the LIVE backend draft produced from the user's real photo (via /ai-log/draft/image).
// There is NO sample/mock draft path: with no draft present the user is routed back to start.

const r1 = (n) => Math.round(n * 10) / 10;
// Safe display label for a sauce/dressing row: prefer a SPECIFIC display_label, else the detector's
// visible label (e.g. "green sauce"), else a generic fallback — never the raw matched record.
const sauceLabelOf = (row) => ((row.display_label && !/^estimated /i.test(row.display_label))
  ? row.display_label
  : (row.detected_name || row.display_label || 'Estimated sauce'));
// Authoritative for rebuilt rows (rebuilt.snapshot); legacy math unchanged (perUnit x amount).
const rowCals = (row) => RR.rowCalories(row);
const rowMacro = (row, k) => RR.rowMacro(row, k);

function SingleFoodCard({ row, onOpenSheet, onRemove, onChangeFood }) {
  const cals = rowCals(row);
  const derived = RR.isDerivedRemainder(row);                        // residual remainder: not editable/removable
  const canChange = RR.isRebuiltRow(row) && !derived;                // rebuilt rows have a Change food action
  const swappable = (row.candidates && row.candidates.length > 1) || canChange;
  const estimated = RR.isEstimatedPortion(row) || row.counting_policy === 'auto_count_estimated' || row.counting_policy === 'estimated_review';
  const disp = RR.displayAmount(row);
  const p = rowMacro(row, 'protein'), c = rowMacro(row, 'carbs'), f = rowMacro(row, 'fat');
  // PHASE 3: structured composed-dish rows show the CONCEPT as the user-facing ingredient name,
  // with the matched FatSecret record kept as supporting detail. Substitutions are marked once.
  const isStructured = row.source_path === 'structured_component';
  const primaryName = (isStructured && row.concept)
    ? cleanLabel(row.concept)
    : (row.component === 'dressings_or_sauce' ? cleanLabel(sauceLabelOf(row)) : (row.display_label || cleanLabel(row.record)));
  const supportRecord = isStructured && row.record
    && cleanLabel(row.record).toLowerCase() !== cleanLabel(row.concept || '').toLowerCase();
  return (
    <div className="bg-card border border-border/40 rounded-lg px-3 py-2.5" data-testid={'food-card-' + row.id}>
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          {/* Hierarchy: name → calories */}
          <div className="flex items-baseline gap-1.5">
            <p className="text-sm font-semibold text-foreground truncate" data-testid={'row-name-' + row.id}>{primaryName}</p>
            {cals != null && <span className="text-sm font-semibold text-foreground shrink-0">· {cals} cal</span>}
          </div>
          {(supportRecord || row.substitution_of) && (
            <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
              {row.substitution_of && (
                <span className="text-[11px] text-muted-foreground" data-testid={'row-substitution-' + row.id}>
                  Replacing {cleanLabel(row.substitution_of)}
                </span>
              )}
              {supportRecord && (
                <span className="text-[11px] text-muted-foreground truncate" data-testid={'row-based-on-' + row.id}>Based on: {cleanLabel(row.record)}</span>
              )}
            </div>
          )}
          {/* Hierarchy: amount → macros — flat, muted, no pills */}
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1">
            {derived ? (
              <span className="text-xs text-muted-foreground" data-testid={'row-portion-' + row.id}>
                {estimated ? '~' : ''}{disp.value} {pluralUnit(disp.unit, disp.value)}
              </span>
            ) : (
              <button onClick={() => onOpenSheet(row.id, 'portion')} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid={'row-portion-' + row.id}>
                {estimated ? '~' : ''}{disp.value} {pluralUnit(disp.unit, disp.value)}
                <Pencil size={9} className="text-muted-foreground/60" />
              </button>
            )}
            {estimated && row.counting_policy !== 'estimated_review' && (
              <span className="text-[11px] text-muted-foreground/80" data-testid={'row-est-' + row.id}>Estimated</span>
            )}
            {canChange && (<button onClick={() => onChangeFood && onChangeFood(row.id, row.component)} className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-accent transition-colors" data-testid={'row-change-food-' + row.id} title="Change food"><Search size={10} />Change food</button>)}
            {p != null && <span className="text-[11px] text-muted-foreground tabular-nums" data-testid={'row-macros-' + row.id}>P{Math.round(p)} · C{Math.round(c)} · F{Math.round(f)}</span>}
          </div>
          {/* Restrained caution: amber text + subtle icon only — never a filled pill */}
          {/* The nutrition-sanity signal is intentionally not shown on cards (approved UI exception to
              source-of-truth Item 18); only the separate estimated_review state is labelled here. */}
          {row.counting_policy === 'estimated_review' && (
            <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 mt-1">
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-600" data-testid={'row-review-' + row.id}><AlertTriangle size={10} />Needs review</span>
            </div>
          )}
          {(() => {
            // Round-6 (§ Issue 1): surface the already-resolved human-readable modifications on the
            // AFFECTED row so the user can see instructions like "made with almond milk" / "no butter"
            // were understood. Per-row => unrelated rows never inherit them; survives every recompute.
            const mods = RR.rowModifications(row);
            return mods.length ? (
              <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-1" data-testid={'row-modifications-' + row.id}>
                {mods.map((m, i) => (
                  <span key={i} className="text-[11px] text-muted-foreground" data-testid={'row-mod-' + row.id + '-' + i}>{m.charAt(0).toUpperCase() + m.slice(1)}</span>
                ))}
              </div>
            ) : null;
          })()}
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {/* Rebuilt rows expose the clearly-labelled "Change food" text action above; the redundant
              swap/refresh icon is shown ONLY for legacy multi-candidate rows (which have no text action). */}
          {swappable && !canChange && (<button onClick={() => onOpenSheet(row.id, 'swap')} className="size-6 rounded-full hover:bg-background flex items-center justify-center text-muted-foreground/70 hover:text-accent" data-testid={'row-swap-' + row.id} title="Swap food"><RefreshCw size={12} /></button>)}
          {!derived && (<button onClick={() => onRemove(row.id)} className="size-6 rounded-full hover:bg-background flex items-center justify-center text-muted-foreground/70 hover:text-destructive" data-testid={'row-delete-' + row.id} title="Remove"><Trash2 size={12} /></button>)}
        </div>
      </div>
    </div>
  );
}

// Compact "Before you log" question card (sauce / generic pending).
function QuestionCard({ row, onQuick, onSearch, onSkip }) {
  const isSauce = row.component === 'dressings_or_sauce';
  // Display-only: for a sauce prompt, use the safe label (never the raw matched FatSecret record).
  // Record/macros are unchanged and still shown as "Based on: …" in the edit/swap sheet.
  const sauceLabel = sauceLabelOf(row);
  const sauceGeneric = /^estimated /i.test(sauceLabel) || /^sauce$/i.test((sauceLabel || '').trim());
  const q = isSauce
    ? (sauceGeneric ? 'Add sauce?' : 'Add ' + cleanLabel(sauceLabel).toLowerCase() + '?')
    : ('Add ' + (row.provisional_label || row.detected_name).replace(/^possible /, '') + '?');
  return (
    <div className="bg-card border border-border/40 rounded-xl p-3.5" data-testid={'review-row-' + row.id}>
      <p className="text-sm font-semibold text-foreground">{q}</p>
      <p className="text-xs text-muted-foreground mt-0.5">Looks like about {row.amount} {cleanLabel(row.unit)}</p>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        {row.candidates.slice(0, 2).map((c) => (
          <button key={c.record} onClick={() => onQuick(row.id, c)} className="text-xs font-medium bg-secondary text-foreground border border-border/40 rounded-full px-3 py-1.5 hover:bg-secondary/70" data-testid={'row-candidate-' + row.id + '-' + c.record.replace(/[^a-z0-9]/gi, '')}>{isSauce ? cleanLabel(sauceLabel) : cleanLabel(c.record)}</button>
        ))}
        <button onClick={() => onSearch(row.id, isSauce ? 'dressings_or_sauce' : null)} className="inline-flex items-center gap-1 text-xs font-medium bg-secondary text-foreground border border-border/40 rounded-full px-3 py-1.5 hover:bg-secondary/70" data-testid={'row-search-' + row.id}><Search size={12} />Search</button>
        <button onClick={() => onSkip(row.id)} className="text-xs font-medium text-muted-foreground px-3 py-1.5 ml-auto" data-testid={'row-skip-' + row.id}>Skip</button>
      </div>
    </div>
  );
}

export default function AIFoodReviewDemo() {
  // PHASE 2 — dual-mode: ?job_id= hydrates from the durable server job; no job_id keeps the existing
  // sessionStorage flow intact. Durable jobs restore date/slot/operation_id/photo from the server.
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('job_id');
  const isJobMode = Boolean(jobId);
  const EMPTY_DRAFT = { rows: [], optional_prompts: [], meal_title: 'Your meal', photo_url: '' };
  // Bug 1c: a durable job review never seeds from the session draft (it belongs to another meal).
  const initial = (isJobMode ? null : getFlowDraft()) || EMPTY_DRAFT;
  const [source, setSource] = React.useState(initial);
  const [rows, setRows] = React.useState((initial.rows || []).filter((r) => !r._disabled));
  const [prompts, setPrompts] = React.useState(initial.optional_prompts || []);
  const [unresolved, setUnresolved] = React.useState(initial.unresolved || []);
  const [residualGroups, setResidualGroups] = React.useState(initial.residual_groups || []);
  const [unrAmt, setUnrAmt] = React.useState(null);   // { identity, value, unit } inline entry for an unresolved food
  const [isRebuiltDraft, setIsRebuiltDraft] = React.useState(Boolean(initial.rebuild_v2));
  // Bug 1c: a durable review renders only once THIS job's READY draft has been applied (see below).
  const [jobHydrated, setJobHydrated] = React.useState(false);
  const [durablePhotoUrl, setDurablePhotoUrl] = React.useState('');   // signed hero URL for durable jobs
  const photoMemRef = React.useRef(null);   // G1: in-memory b64 for recompute (NEVER persisted)
  const [sheet, setSheet] = React.useState(null);   // portion/swap sheet: { id, mode }
  const [search, setSearch] = React.useState(null); // food search sheet: { prefer, title, targetRowId }
  // Lift the fixed portion sheet above the iOS keyboard while its Amount field is being typed in (same
  // as FoodSearchSheet). With the keyboard open the home-indicator safe area is covered, so only the
  // normal padding remains.
  const kb = useKeyboardInset();
  const [logState, setLogState] = React.useState(null); // null | 'confirm-pending' | { committed, dry_run, ... }
  const [commitAvailable, setCommitAvailable] = React.useState(false); // server-computed real-write signal
  const [rebuildCommitAvailable, setRebuildCommitAvailable] = React.useState(false); // ITEM-19 rebuild real-write signal
  const [committing, setCommitting] = React.useState(false);           // in-flight guard (no double-submit)
  const [committedDone, setCommittedDone] = React.useState(false);     // a real write already happened
  // PORTION editor (rebuilt rows): LOCAL draft state so typing / +/- / serving selection never fire a
  // recompute; exactly ONE recompute is sent on Done. A monotonically increasing seq guards against
  // stale / out-of-order responses ever overwriting newer authoritative review state.
  const [portionDraft, setPortionDraft] = React.useState(null);
  const [savingPortion, setSavingPortion] = React.useState(false);
  // Frontend-only feedback for a rebuilt "add food" while its backend recompute runs. NEVER part of
  // `rows`: not sent to the backend, not counted in any total, no macros. Cleared only when the
  // authoritative rows are applied; kept with Try again / Dismiss when the add fails or isn't applied.
  const [pendingAdd, setPendingAdd] = React.useState(null);   // null | { food, status: 'pending' | 'error' }
  const addingRef = React.useRef(false);                       // sync guard: one add_food in flight
  const recomputeSeqRef = React.useRef(0);
  // Stable idempotency key for THIS review session: a retried Log Meal (network retry / double tap)
  // reuses it so the backend returns the original meal instead of inserting a duplicate.
  const opIdRef = React.useRef('rbop_' + Math.random().toString(36).slice(2) + Date.now().toString(36));
  const commitOpIdRef = React.useRef(null);   // durable jobs override with job.operation_id
  const navigate = useNavigate();
  // Reactive LOCAL current day (shared with Home): updates at local midnight / app-resume /
  // window focus, so AI logging always resolves to the same day Home is showing.
  const liveToday = useToday();
  // Meal slot to log into. LEGACY: defaulted by device local time. DURABLE: overridden on hydration
  // with job.suggested_meal_type (frozen from the ORIGINAL submission-local time) — never review-time.
  const defaultMealSlot = () => {
    const h = new Date().getHours();
    if (h >= 4 && h < 11) return 'Breakfast';
    if (h >= 11 && h < 16) return 'Lunch';
    if (h >= 16 && h < 22) return 'Dinner';
    return 'Snack';
  };
  const [mealSlot, setMealSlot] = React.useState(defaultMealSlot);
  // Meal DATE. LEGACY: falls back to the reactive local today. DURABLE: set on hydration from
  // job.submitted_local_date (frozen at submission) — never derived from when Review is opened.
  const [mealDate, setMealDate] = React.useState(null);
  const effectiveDate = mealDate || liveToday;
  const checkRef = React.useRef(null);
  // DURABLE: only the fetched job's signed URL (its draft.photo_url is a storage key, not a URL) — never a
  // legacy session photo. LEGACY: unchanged (session photo, else the draft's photo_url).
  const heroPhoto = isJobMode ? durablePhotoUrl : (getFlowPhoto() || source.photo_url);

  React.useEffect(() => {
    let alive = true;
    if (isJobMode) {
      // DURABLE: hydrate the review from the server-backed durable job (survives app close/restart).
      (async () => {
        try {
          const { data: job } = await api.get('/ai-log/jobs/' + jobId);
          if (!alive) return;
          if (job.status !== 'ready' || !job.draft) { navigate('/ai-log', { replace: true }); return; }
          const d = job.draft;
          setSource(d);
          setRows((d.rows || []).filter((r) => !r._disabled));
          setPrompts(d.optional_prompts || []);
          setUnresolved(d.unresolved || []);
          setResidualGroups(d.residual_groups || []);
          setIsRebuiltDraft(Boolean(d.rebuild_v2));
          if (job.submitted_local_date) setMealDate(job.submitted_local_date);   // frozen default
          if (job.suggested_meal_type) setMealSlot(job.suggested_meal_type);     // frozen default
          if (job.operation_id) commitOpIdRef.current = job.operation_id;        // durable idempotency
          if (job.image_url) setDurablePhotoUrl(job.image_url);
          setJobHydrated(true);                                                  // Bug 1c: review may render
          // G1: restore the original photo into MEMORY so the existing edit/recompute path keeps its
          // photo-assisted behavior on a delayed review. Never persisted back into the job/draft.
          api.get('/ai-log/jobs/' + jobId + '/photo-b64')
            .then((r) => { if (alive && r.data && r.data.photo_b64) photoMemRef.current = r.data.photo_b64; })
            .catch(() => {});
        } catch (e) {
          if (alive) navigate('/ai-log', { replace: true });
        }
      })();
    } else if (!getFlowDraft()) {
      // LEGACY: no live draft (deep-link / refresh) -> nothing to review; never fabricate one.
      navigate('/ai-log', { replace: true });
      return () => { alive = false; };
    }
    api.get('/ai-log/availability')
      .then((r) => { if (alive) { setCommitAvailable(!!(r.data && r.data.commit_available));
                                   setRebuildCommitAvailable(!!(r.data && r.data.rebuild_v2_commit_available)); } })
      .catch(() => {});
    return () => { alive = false; };
  }, [navigate, isJobMode, jobId]);

  // Apply a rebuild-v2 review edit on the BACKEND (never local perUnit x amount for rebuilt rows).
  // Recognition is not re-run. Unresolved foods + residual review state are carried through every call, and
  // rows + unresolved + residual groups + subtotal are rebuilt from the returned authoritative draft.
  const recomputeRebuilt = async (edit, seq) => {
    const base = source.context || { meal_title: source.meal_title, restaurant: source.restaurant, photo_ref: source.photo_url, raw_user_text: source.raw_user_text };
    // Thread the ORIGINAL downscaled photo TRANSIENTLY (never persisted) so an already-selected provider
    // identity can run the approved targeted quantity-equivalence AI after a review amount edit. We strip
    // any data-URL prefix; Recognition is NEVER re-run.
    // G1: a durable review uses ONLY its own in-memory job photo, never a legacy session image.
    const rawImg = isJobMode ? photoMemRef.current : (getFlowImageB64() || photoMemRef.current);
    const photoB64 = rawImg ? rawImg.split(',').pop() : (base.photo || null);
    const ctx = { ...base, photo: photoB64 };
    const res = await api.post('/ai-log/draft/rebuild-v2/recompute',
      { context: ctx, entries: RR.entriesFromRows(rows), unresolved, residual_groups: residualGroups, edit });
    const d = res.data && res.data.draft;
    // Stale / out-of-order guard: when a seq is supplied, a superseded recompute response must NEVER
    // overwrite newer authoritative review state (§ portion-editor race protection).
    if (d && (seq == null || seq === recomputeSeqRef.current)) {
      // prefer the FRESH sanitized context from the backend (parent identity/amount/members/homemade can
      // change on a recompute); fall back to the prior one. The base64 photo is never stored here.
      setSource((s) => ({ ...s, ...d, context: d.context || (s && s.context) }));
      setRows((d.rows || []).filter((r) => !r._disabled));
      setUnresolved(d.unresolved || []);
      setResidualGroups(d.residual_groups || []);
    }
    return d;
  };

  // While an addition is pending, other review edits wait: each recompute is built from the current rows,
  // and the add's response replaces rows, so an overlapping edit would drop the addition or be undone by it.
  const waitForPendingAdd = () => {
    if (!addingRef.current) return false;
    toast('Still adding to your meal…');
    return true;
  };

  // Rebuild-v2 add: show the pending row now, then the EXISTING add_food recompute — sequenced, so a stale
  // response never overwrites newer review state. The confirmed row + totals come only from the backend.
  const addRebuiltFood = (food) => {
    if (addingRef.current) return;                             // repeated taps: no duplicate add_food
    addingRef.current = true;
    const pid = food.fatsecret_food_id != null ? String(food.fatsecret_food_id) : (food.external_id != null ? String(food.external_id) : null);
    const seq = ++recomputeSeqRef.current;
    setPendingAdd({ food, status: 'pending' });
    recomputeRebuilt({ op: 'add_food', identity: food.record,
      provider_identity: pid ? { id: pid, namespace: 'fatsecret_v5' } : null,
      amount: { value: food.amount, unit: food.unit, source: 'user', approximate: false } }, seq)
      .then((d) => {
        if (d && seq === recomputeSeqRef.current) {            // authoritative rows were applied
          setPendingAdd(null);
          toast.success('Added ' + food.record);
        } else {
          setPendingAdd({ food, status: 'error' });            // no draft / superseded: not in the meal
        }
      })
      .catch((e) => {
        setPendingAdd({ food, status: 'error' });
        toast.error('Add failed: ' + (e.response?.data?.detail || e.message));
      })
      .finally(() => { addingRef.current = false; });
  };

  // Supply the missing amount for an unresolved rebuild-v2 food -> backend continues resolution from the
  // already-recognized meaning (provider-first, else Role-A). No Recognition re-run. No fabricated macros.
  const resolveUnresolved = async () => {
    if (waitForPendingAdd()) return;
    if (!unrAmt || !(unrAmt.value > 0) || !String(unrAmt.unit || '').trim()) return;
    const id = unrAmt.identity;
    try {
      await recomputeRebuilt({ op: 'resolve_unresolved', identity: id, node_id: unrAmt.node_id || null,
        amount: { value: Number(unrAmt.value), unit: String(unrAmt.unit).trim(), source: 'user', approximate: false } });
      setUnrAmt(null);
      toast.success('Added ' + cleanLabel(id));
    } catch (e) { toast.error('Could not add: ' + (e.response?.data?.detail || e.message)); }
  };

  // Opening the PORTION editor seeds a LOCAL draft from the AUTHORITATIVE amount (never legacy compat
  // `row.amount`). All edits stay local until the user taps Done, which sends exactly ONE recompute.
  const openSheet = (id, mode) => {
    if (waitForPendingAdd()) return;
    if (mode === 'portion') {
      const target = rows.find((r) => r.id === id);
      setPortionDraft(target ? RR.seedPortionDraft(target) : null);
    }
    setSheet({ id, mode });
  };
  const closeSheet = () => { setSheet(null); setPortionDraft(null); setSavingPortion(false); };

  // Rebuilt rows recompute on the backend; legacy rows keep local perUnit math. A residual anchor edit
  // reruns the WHOLE residual group (anchor + derived remainder); a DERIVED remainder is not editable.
  const editPortion = (id, patch) => {
    const target = rows.find((r) => r.id === id);
    if (target && RR.isRebuiltRow(target)) {
      if (RR.isDerivedRemainder(target)) { toast('The remaining part updates automatically'); return; }
      const cur = RR.displayAmount(target);
      const nextAmt = { value: (patch.amount != null ? patch.amount : cur.value), unit: (patch.unit || cur.unit), state: (target.rebuilt.amount || {}).state, source: 'user', approximate: false };
      const op = RR.isResidualAnchor(target) ? 'residual_anchor_amount' : 'amount';
      recomputeRebuilt({ id, op, amount: nextAmt }).catch((e) => toast.error('Recompute failed: ' + (e.response?.data?.detail || e.message)));
      return;
    }
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch, portion_estimated: true } : r)));
  };
  // Commit the LOCAL portion draft for a rebuilt row: validate, guard against double-submit, send exactly
  // ONE recompute, then update from the returned authoritative draft. On failure keep the editor open and
  // preserve the user's draft (close ONLY on success). A stale response can never overwrite newer state.
  const commitPortion = async () => {
    const target = rows.find((r) => r.id === (sheet && sheet.id));
    if (!target || !RR.isRebuiltRow(target)) { closeSheet(); return; }
    if (RR.isDerivedRemainder(target)) { toast('The remaining part updates automatically'); return; }
    if (!RR.canSubmitPortion(savingPortion, portionDraft)) return;      // double-submit / invalid guard
    if (!RR.portionDraftChanged(target, portionDraft)) { closeSheet(); return; }
    const edit = RR.buildPortionEdit(target, portionDraft);
    const seq = ++recomputeSeqRef.current;
    setSavingPortion(true);
    try {
      await recomputeRebuilt(edit, seq);
      if (seq === recomputeSeqRef.current) closeSheet();
    } catch (e) {
      toast.error('Recompute failed: ' + (e.response?.data?.detail || e.message));
      if (seq === recomputeSeqRef.current) setSavingPortion(false);     // keep editor open + draft preserved
    }
  };
  // Rebuilt rows: a residual ANCHOR removal must recompute the residual group on the backend (the derived
  // remainder changes with it); a DERIVED remainder can never be independently removed; an ordinary
  // independent rebuilt row (and any legacy row) is removed normally.
  const removeRow = (id) => {
    if (waitForPendingAdd()) return;
    const target = rows.find((r) => r.id === id);
    if (target && RR.isRebuiltRow(target)) {
      if (RR.isDerivedRemainder(target)) { toast('The remaining part is calculated automatically'); return; }
      if (RR.isResidualAnchor(target)) {
        recomputeRebuilt({ id, op: 'residual_anchor_remove' })
          .then(() => toast('Removed')).catch((e) => toast.error('Remove failed: ' + (e.response?.data?.detail || e.message)));
        return;
      }
    }
    setRows((rs) => rs.filter((r) => r.id !== id)); toast('Removed');
  };

  // Change/Search a rebuilt row's food through the EXISTING FoodSearchSheet (no local candidates needed).
  const changeFood = (id, component) => openSearch(id, component, 'Change food');
  const skipRow = (id) => { if (waitForPendingAdd()) return; setRows((rs) => rs.filter((r) => r.id !== id)); toast('Skipped'); };

  // Resolve a pending row (e.g. sauce) with a chosen candidate record.
  const confirmCandidate = (id, cand) => {
    if (waitForPendingAdd()) return;
    // Sauce rows show the safer detected/display label in the toast (never the raw generic record).
    const target = rows.find((r) => r.id === id);
    const friendly = (target && target.component === 'dressings_or_sauce')
      ? cleanLabel(sauceLabelOf(target))
      : cleanLabel(cand.record);
    // Replace ALL identity-dependent fields together (record/macros/IDs) so no stale FatSecret id
    // from the previous match survives; keep this component's structured identity + row id.
    setRows((rs) => rs.map((r) => (r.id === id
      ? { ...swapRowIdentity(r, {
            record: cand.record, unit: cand.unit, amount: cand.amount, perUnit: cand.perUnit,
            // serving options must belong ONLY to the newly selected record — never reuse the old
            // row's picker. Use the candidate's own options, else build one from the candidate.
            serving_options: (cand.serving_options && cand.serving_options.length)
              ? cand.serving_options
              : [{ unit: cand.unit, size: cand.amount, perUnit: cand.perUnit }],
            candidates: r.candidates,
            fatsecret_food_id: cand.fatsecret_food_id != null ? cand.fatsecret_food_id : null,
            external_id: cand.external_id != null ? cand.external_id : null,
            match_source: cand.match_source || 'fatsecret_candidate',
            brand: cand.brand != null ? cand.brand : null,
            category: cand.category != null ? cand.category : null,
            component: cand.component,
          }) }
      : r)));
    closeSheet();
    toast.success('Added ' + friendly);
  };

  // Cooking fat — olive oil / butter are DISTINCT records with distinct macros.
  const addFat = (record, unit) => {
    if (waitForPendingAdd()) return;
    const food = findFood(record);
    const opt = food.servings.find((o) => o.unit === unit) || food.servings[0];
    setRows((rs) => rs.concat([{ id: 'fat-' + Date.now(), detected_name: record, status: 'confirmed', counting_policy: 'auto_count_estimated', included_in_subtotal_by_default: true, user_action: 'optional_edit', portion_estimated: true, record, amount: 1, unit, perUnit: opt.perUnit, serving_options: food.servings.map((o) => ({ unit: o.unit, size: o.size, perUnit: o.perUnit })), candidates: [], component: 'fats_or_rich' }]));
    setPrompts((ps) => ps.filter((x) => x.type !== 'hidden_fat'));
    toast.success('Added ' + record);
  };

  // Result chosen from the real read-only FatSecret search (macros come from the chosen record).
  const onSearchAdd = (food) => {
    const pid = food.fatsecret_food_id != null ? String(food.fatsecret_food_id) : (food.external_id != null ? String(food.external_id) : null);
    if (isRebuiltDraft) {
      // Rebuild-v2: swap/add MUST stay authoritative (through the backend) — never a legacy-only row.
      if (search && search.targetRowId) {
        const target = rows.find((r) => r.id === search.targetRowId);
        const cur = RR.displayAmount(target);             // preserve the reviewed physical amount
        // §10: changing the matched food/provider does NOT mean the user confirmed the physical amount.
        // Preserve the EXISTING amount's source/approximate/state (do not falsify user-confirmed).
        const curState = (target.rebuilt && target.rebuilt.amount && target.rebuilt.amount.state) || undefined;
        const op = RR.isResidualAnchor(target) ? 'residual_anchor_provider' : 'provider';
        recomputeRebuilt({ id: target.id, op, identity: food.record, semantic_identity: food.record,
          provider_identity: pid ? { id: pid, namespace: 'fatsecret_v5' } : null,
          amount: { value: cur.value, unit: cur.unit, source: cur.source || 'estimated', approximate: !!cur.approximate, ...(curState ? { state: curState } : {}) } })
          .then(() => toast.success('Swapped to ' + food.record))
          .catch((e) => toast.error('Swap failed: ' + (e.response?.data?.detail || e.message)));
      } else {
        addRebuiltFood(food);
      }
      setSearch(null);
      return;
    }
    if (search && search.targetRowId) {
      // swap/replace: replace every identity-dependent field together (no stale FatSecret id)
      setRows((rs) => rs.map((r) => (r.id === search.targetRowId ? swapRowIdentity(r, food) : r)));
    } else {
      // PHASE 3: a hidden-ingredient prompt (milk/butter/...) becomes a NORMAL editable, counted row.
      const ctx = (search && search.concept)
        ? { source_path: 'structured_component', concept: search.concept, role: search.role || null, prefer: search.prefer }
        : { prefer: search && search.prefer };
      setRows((rs) => rs.concat([buildAddedRow(food, ctx)]));
      if (search && search.prefer === 'fats_or_rich') setPrompts((ps) => ps.filter((x) => x.type !== 'hidden_fat'));
      // remove the SPECIFIC opt-in prompt that opened this search, so it can't be added twice
      if (search && search.promptId) setPrompts((ps) => ps.filter((x) => x.id !== search.promptId));
    }
    toast.success('Added ' + food.record);
    setSearch(null);
  };

  const openSearch = (targetRowId, prefer, title, opts = {}) => {
    if (waitForPendingAdd()) return;
    setSearch({ targetRowId, prefer, title, ...opts });
  };

  // Accept an estimated_review row -> promote to a counted (estimated) row so it logs on commit.
  const acceptEstimated = (id) => {
    if (waitForPendingAdd()) return;
    setRows((rs) => rs.map((r) => (r.id === id
      ? { ...r, accepted: true, counting_policy: 'auto_count_estimated', included_in_subtotal_by_default: true, user_action: 'optional_edit' }
      : r)));
    toast.success('Kept in meal');
  };

  // Commit (dry-run) — maps the CURRENT reviewed rows to the log payload. No DB write while
  // AI_LOG_COMMIT_ENABLED is off; the endpoint returns exactly what WOULD be logged.
  const doCommit = async () => {
    if (committing || committedDone) return;        // guard: never double-submit a real write
    if (waitForPendingAdd()) return;                 // never log without the addition the user just made
    const mt = mealSlot;                             // user-selected meal slot (defaulted by local time)
    // Rebuild-v2 meals NEVER go through the legacy write path during Patch 2 (Item 19 is later): use the
    // confirmation-only, NO-DB rebuild endpoint that returns the would-log summary from authoritative snapshots.
    if (isRebuiltDraft) {
      setCommitting(true);
      try {
        const ctx = source.context || { meal_title: source.meal_title, restaurant: source.restaurant,
          photo_ref: source.photo_url, raw_user_text: source.raw_user_text,
          parent_identity: (source.parent_context && source.parent_context.parent_identity) || null,
          parent_amount: (source.parent_context && source.parent_context.parent_amount) || null };
        if (rebuildCommitAvailable) {
          // ITEM-19 real write via the DEDICATED rebuild-v2 commit endpoint (never legacy perUnit rows).
          const res = await api.post('/ai-log/draft/rebuild-v2/commit',
            { context: ctx, entries: RR.entriesFromRows(rows), unresolved, residual_groups: residualGroups,
              date: effectiveDate, meal_type: mt, image_url: source.photo_url || null,
              client_today: liveToday, operation_id: commitOpIdRef.current || opIdRef.current,
              job_id: jobId || null });
          setLogState({ committed: true, logged_count: res.data.logged_count, totals: res.data.totals || {}, excluded: res.data.excluded || [] });
          setCommittedDone(true);                     // lock further commits for this meal
          toast.success('Logged ' + res.data.totals.calories + ' cal to ' + mt);
          setTimeout(() => navigate('/home'), 900);   // brief success, then back to Home/meal plan
        } else {
          // preview-only (rebuild real-write flag OFF): confirmation boundary, NO DB write.
          const res = await api.post('/ai-log/draft/rebuild-v2/confirm',
            { context: ctx, entries: RR.entriesFromRows(rows), unresolved, residual_groups: residualGroups, edit: {} });
          setLogState({ committed: false, commit_enabled: false, would_write_count: res.data.would_log_count,
                        totals: res.data.totals, excluded: [] });
          toast.success('Preview: would log ' + res.data.would_log_count + ' item' + (res.data.would_log_count === 1 ? '' : 's') + ' · ' + res.data.totals.calories + ' cal to ' + mt);
        }
      } catch (e) {
        toast.error('Log failed: ' + (e.response?.data?.detail || e.message));
      } finally { setCommitting(false); }
      return;
    }
    const date = effectiveDate;                      // selected meal date (defaults to local today)
    const dry = !commitAvailable;                    // real write ONLY when the server signals it
    // client_today = the ACTUAL current local day; may differ from a back-dated meal date.
    const client_today = liveToday;
    setCommitting(true);
    try {
      const res = await api.post('/ai-log/commit?dry_run=' + (dry ? 'true' : 'false'),
        { date, meal_type: mt, meal_title: source.meal_title, image_url: source.photo_url || null,
          chain: source.chain || null, route: source.route || null, debug_id: source.debug_id || null,
          client_today, rows });
      setLogState(res.data);
      if (res.data.committed) {
        setCommittedDone(true);                       // lock further commits for this meal
        const n = res.data.logged_count;
        toast.success('Logged ' + n + ' item' + (n === 1 ? '' : 's') + ' · ' + res.data.totals.calories + ' cal to ' + mt);
        setTimeout(() => navigate('/home'), 900);     // brief success, then back to Home/meal plan
      } else {
        const n = res.data.would_write_count != null ? res.data.would_write_count : 0;
        toast.success('Preview: would log ' + n + ' item' + (n === 1 ? '' : 's') + ' · ' + res.data.totals.calories + ' cal to ' + mt);
      }
    } catch (e) {
      toast.error('Log failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setCommitting(false);
    }
  };
  // (bottom Log button calls doCommit directly — no separate review/confirm step)

  // Phase A/B: partition by counting policy (fallback to legacy status for older drafts).
  const isIncluded = (r) => (r.included_in_subtotal_by_default != null
    ? r.included_in_subtotal_by_default : r.status === 'confirmed');
  const needsAction = (r) => (r.user_action ? r.user_action === 'needs_action'
    : (r.status === 'pending' || r.status === 'unmatched'));

  const counted = rows.filter(isIncluded);
  const estimatedReview = rows.filter((r) => r.counting_policy === 'estimated_review');
  const checks = rows.filter((r) => needsAction(r) && !isIncluded(r) && r.counting_policy !== 'estimated_review');
  // Show every counted item as its own transparent row (no collapsed grouping). Non-veg first,
  // then toppings/veg — each remains individually editable/removable.
  const orderedCounted = [...counted.filter((r) => r.component !== 'vegetables'),
                          ...counted.filter((r) => r.component === 'vegetables')];
  const fatPrompt = prompts.find((p) => p.type === 'hidden_fat');

  const subtotal = counted.reduce((a, r) => a + (rowCals(r) || 0), 0);
  const macros = ['protein', 'carbs', 'fat'].reduce((acc, k) => { acc[k] = Math.round(counted.reduce((a, r) => a + (rowMacro(r, k) || 0), 0)); return acc; }, {});
  // Visible high-impact items awaiting review: `ask` rows with a real matched record + calories
  // (e.g. a visible dressing/drink). They are NOT in the counted subtotal, so surface them so the
  // user doesn't read the counted number as the full meal. Optional hidden-fat prompts are excluded.
  const reviewItems = rows.filter((r) => (r.counting_policy === 'ask' || r.counting_policy === 'estimated_review') && r.record && r.perUnit);
  const reviewCals = Math.round(reviewItems.reduce((a, r) => a + ((r.perUnit.calories || 0) * r.amount), 0));
  const reviewCount = reviewItems.length;
  // Required-action count only. Optional prompts (e.g. "Add cooking fat?") still render
  // in the checklist but must NOT count as required actions.
  const nChecks = checks.length + estimatedReview.length;
  const scrollToChecks = () => checkRef.current && checkRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const sheetRow = sheet ? rows.find((r) => r.id === sheet.id) : null;
  const sheetRebuilt = Boolean(sheetRow && RR.isRebuiltRow(sheetRow));
  // Serving choices: rebuilt rows use ONLY the trusted provider serving options carried on the row (from
  // the locked FSID via _build_serving_options); legacy rows keep their existing serving_options.
  const sheetServingOptions = sheetRow ? (sheetRebuilt ? RR.providerServingOptions(sheetRow) : (sheetRow.serving_options || [])) : [];
  // Show the Serving picker when at least one TRUSTED provider option is a meaningful alternative to the
  // row's current physical amount (rebuilt); legacy keeps its existing >1 behavior. Never fabricates.
  const sheetServingVisible = sheetRow
    ? (sheetRebuilt
        ? RR.meaningfulServingOptions(sheetServingOptions, RR.displayAmount(sheetRow),
            sheetRow.rebuilt && sheetRow.rebuilt.origin === 'ai' && !!sheetRow.rebuilt.matched_provider_identity).length >= 1
        : sheetServingOptions.length > 1)
    : false;

  // Recoverable state: a food WAS detected but NO valid nutrition record resolved. We trust the
  // backend contract as the source of truth (detection_status / has_usable_nutrition). A valid
  // zero-calorie meal (water, black coffee, diet drink) is `ok` and MUST stay loggable even though
  // its subtotal is 0 — we never re-derive "failure" from subtotal <= 0 when the backend says ok.
  const detectedNames = source.detected_food_names || [];
  const backendKnowsStatus = source.detection_status != null || source.has_usable_nutrition != null;
  const backendOk = source.detection_status === 'ok' || source.has_usable_nutrition === true;
  const backendNeedsSelection = source.detection_status === 'needs_selection' || source.has_usable_nutrition === false;
  // Defensive fallback for OLD contracts that predate these fields: only infer failure when the
  // backend gives us nothing to trust.
  const legacyNoNutrition = !backendKnowsStatus
    && subtotal <= 0 && reviewItems.length === 0 && estimatedReview.length === 0 && checks.length === 0;
  const needsSelection = !backendOk && (backendNeedsSelection || legacyNoNutrition);

  // Bug 1c: until THIS job's server data has loaded, show only the neutral AI Log loading screen (same markup
  // as AILogStart's availability check) — no photo, title, nutrition, date/slot, edit or Log controls.
  // A missing / non-ready / failed job keeps the existing redirect to /ai-log.
  if (isJobMode && !jobHydrated) {
    return (
      <div className="min-h-screen bg-background flex flex-col" data-testid="ai-review-loading">
        <div className="header-safe-top px-4 pb-2 flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="size-9 rounded-full bg-card border border-border/40 flex items-center justify-center" data-testid="review-loading-back"><ArrowLeft size={18} /></button>
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-accent"><Sparkles size={13} /> Log with AI</span>
        </div>
        <div className="flex-1 flex items-center justify-center" role="status" aria-label="Loading">
          <Loader2 size={26} className="text-accent animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="ai-food-review-demo">
      {/* Photo hero */}
      <div className="relative">
        {heroPhoto ? (
          <img src={heroPhoto} alt={source.meal_title} className="w-full h-56 object-cover" data-testid="meal-photo" />
        ) : (
          <div className="w-full h-56 bg-secondary" data-testid="meal-photo-placeholder" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-black/20" />
        <button onClick={() => window.history.back()} className="absolute top-safe left-4 size-9 rounded-full bg-black/40 backdrop-blur flex items-center justify-center text-white" data-testid="review-back"><ArrowLeft size={18} /></button>
        <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
          <h1 className="text-xl font-bold leading-tight" data-testid="meal-title">{source.meal_title}</h1>
          <div className="flex items-end gap-2 mt-1">
            <span className="text-2xl font-extrabold leading-none" data-testid="confident-subtotal">{subtotal}</span>
            <span className="text-sm text-white/85 mb-0.5">cal · estimate</span>
          </div>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-white/90">
            <span data-testid="subtotal-protein">Protein {macros.protein}g</span>
            <span data-testid="subtotal-carbs">Carbs {macros.carbs}g</span>
            <span data-testid="subtotal-fat">Fat {macros.fat}g</span>
          </div>
          {reviewCount > 0 && (
            <p className="mt-1.5 text-xs font-medium text-amber-300" data-testid="pending-review-line">
              {subtotal} cal counted · +{reviewCals} cal needs review ({reviewCount} {reviewCount === 1 ? 'item' : 'items'})
            </p>
          )}
          {nChecks > 0 && (
            <button onClick={scrollToChecks} className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium bg-white/15 backdrop-blur rounded-full px-2.5 py-1" data-testid="items-to-check">{nChecks} {nChecks === 1 ? 'item' : 'items'} to check <ChevronDown size={12} /></button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {/* Recoverable: food detected but no usable nutrition -> search/select, never a 0-cal meal */}
        {needsSelection && (
          <section className="bg-card border border-border/40 rounded-xl p-4" data-testid="needs-selection-panel">
            <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-600" data-testid="needs-selection-title"><AlertTriangle size={14} />We couldn’t find nutrition for this meal</p>
            <p className="text-xs text-muted-foreground mt-1" data-testid="needs-selection-body">
              {detectedNames.length > 0
                ? `We spotted ${detectedNames.slice(0, 3).join(', ')} but couldn’t match it to nutrition data. Search and pick the food so we can estimate it.`
                : 'Search and pick the food so we can estimate it.'}
            </p>
            <button onClick={() => openSearch(null, null, detectedNames[0] ? ('Search: ' + detectedNames[0]) : 'Search food')}
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold bg-accent text-white rounded-full px-4 py-2" data-testid="needs-selection-search-btn">
              <Search size={14} />Search {detectedNames[0] ? detectedNames[0] : 'food'}
            </button>
          </section>
        )}

        {/* Added to meal — every counted item is its own visible, editable row */}
        <section data-testid="section-added">
          <p className="text-[13px] font-semibold text-foreground mb-2">Added to meal</p>
          <div className="space-y-1.5">
            {orderedCounted.map((r) => (<SingleFoodCard key={r.id} row={r} onOpenSheet={openSheet} onRemove={removeRow} onChangeFood={changeFood} />))}
          </div>
        </section>

        {/* Needs review — visible high-impact items with an estimated (uncertain) identity */}
        {estimatedReview.length > 0 && (
          <section data-testid="section-estimated-review">
            <p className="text-[13px] font-semibold text-foreground mb-1">Needs review</p>
            <p className="text-xs text-muted-foreground mb-2">Visible, but the exact match is our best guess — check, swap or remove.</p>
            <div className="space-y-2">
              {estimatedReview.map((r) => (
                <div key={r.id} className="space-y-1">
                  <SingleFoodCard row={r} onOpenSheet={openSheet} onRemove={removeRow} onChangeFood={changeFood} />
                  {r.portion_display && (
                    <p className="text-[11px] font-medium text-amber-600 pl-1" data-testid={'row-portion-display-' + r.id}>{r.portion_display}</p>
                  )}
                  <div className="flex items-center gap-3 pl-1">
                    <button onClick={() => acceptEstimated(r.id)} className="inline-flex items-center gap-1 text-[11px] font-medium text-accent" data-testid={'row-accept-' + r.id}><Check size={11} />Keep &amp; log</button>
                    <button onClick={() => openSearch(r.id, r.component, 'Change item')} className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground" data-testid={'row-change-' + r.id}><RefreshCw size={11} />Change</button>
                    <button onClick={() => skipRow(r.id)} className="text-[11px] font-medium text-muted-foreground ml-auto" data-testid={'row-skip-' + r.id}>Skip</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Before you log — compact checklist */}
        <section ref={checkRef} data-testid="section-before-log">
          <p className="text-[13px] font-semibold text-foreground mb-2">Before you log</p>
          <div className="space-y-2">
            {/* Rebuild-v2 unresolved foods: visible, NO fabricated macros, excluded from subtotal, block log */}
            {unresolved.map((u, i) => (
              <div key={'unres-' + i} className="bg-card border border-amber-300/60 rounded-xl p-3.5" data-testid={'unresolved-row-' + i}>
                <p className="text-sm font-semibold text-foreground">How much {cleanLabel(u.identity)}?</p>
                <p className="text-xs text-muted-foreground mt-0.5" data-testid={'unresolved-nomacros-' + i}>We can’t estimate nutrition until you set the amount.</p>
                {unrAmt && (unrAmt.node_id ? unrAmt.node_id === u.node_id : unrAmt.identity === u.identity) ? (
                  <div className="flex items-center gap-2 mt-3" data-testid={'unresolved-entry-' + i}>
                    <input type="number" inputMode="decimal" min="0" step="0.25" placeholder="Amount"
                      value={unrAmt.value || ''} onChange={(e) => setUnrAmt((a) => ({ ...a, value: e.target.value }))}
                      className="w-20 bg-background border border-border/50 rounded-lg py-1 px-2 text-sm outline-none focus:border-accent" data-testid={'unresolved-value-' + i} />
                    <input type="text" placeholder="unit (oz, cup, slice)"
                      value={unrAmt.unit || ''} onChange={(e) => setUnrAmt((a) => ({ ...a, unit: e.target.value }))}
                      className="flex-1 min-w-0 bg-background border border-border/50 rounded-lg py-1 px-2 text-sm outline-none focus:border-accent" data-testid={'unresolved-unit-' + i} />
                    <button onClick={resolveUnresolved} className="text-xs font-semibold bg-accent text-white rounded-full px-3 py-1.5" data-testid={'unresolved-confirm-' + i}>Add</button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <button onClick={() => setUnrAmt({ identity: u.identity, node_id: u.node_id || null, value: '', unit: '' })} className="inline-flex items-center gap-1 text-xs font-medium bg-secondary text-foreground border border-border/40 rounded-full px-3 py-1.5 hover:bg-secondary/70" data-testid={'unresolved-set-' + i}><Pencil size={12} />Set amount</button>
                    <button onClick={() => { if (waitForPendingAdd()) return; setUnresolved((us) => us.filter((x) => (u.node_id ? x.node_id !== u.node_id : x.identity !== u.identity))); }} className="text-xs font-medium text-muted-foreground px-3 py-1.5 ml-auto" data-testid={'unresolved-skip-' + i}>Skip</button>
                  </div>
                )}
              </div>
            ))}
            {checks.map((r) => (<QuestionCard key={r.id} row={r} onQuick={confirmCandidate} onSearch={openSearch} onSkip={skipRow} />))}

            {fatPrompt && (
              <div className="bg-card border border-border/40 rounded-xl p-3.5" data-testid="prompt-cooking-fat">
                <p className="text-sm font-semibold text-foreground">Add cooking fat?</p>
                <p className="text-xs text-muted-foreground mt-0.5">Optional — image-only can’t see added fats. Olive oil and butter differ, so pick one.</p>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <button onClick={() => addFat('Olive Oil', 'tsp')} className="text-xs font-medium bg-secondary text-foreground border border-border/40 rounded-full px-3 py-1.5 hover:bg-secondary/70" data-testid="fat-oliveoil-tsp">Olive oil 1 tsp</button>
                  <button onClick={() => addFat('Olive Oil', 'tbsp')} className="text-xs font-medium bg-secondary text-foreground border border-border/40 rounded-full px-3 py-1.5 hover:bg-secondary/70" data-testid="fat-oliveoil-tbsp">Olive oil 1 tbsp</button>
                  <button onClick={() => addFat('Butter', 'tsp')} className="text-xs font-medium bg-secondary text-foreground border border-border/40 rounded-full px-3 py-1.5 hover:bg-secondary/70" data-testid="fat-butter">Butter</button>
                  <button onClick={() => openSearch(null, 'fats_or_rich', 'Add cooking fat')} className="inline-flex items-center gap-1 text-xs font-medium bg-secondary text-foreground border border-border/40 rounded-full px-3 py-1.5 hover:bg-secondary/70" data-testid="fat-search"><Search size={12} />Search</button>
                  <button onClick={() => setPrompts((ps) => ps.filter((x) => x.type !== 'hidden_fat'))} className="text-xs font-medium text-muted-foreground px-3 py-1.5 ml-auto" data-testid="fat-skip">Skip</button>
                </div>
              </div>
            )}

            {prompts.filter((p) => p.type === 'add_visible').map((p) => (
              <div key={p.id} className="bg-card border border-border/40 rounded-xl p-3.5" data-testid={'prompt-add-visible-' + p.id}>
                <p className="text-sm font-semibold text-foreground">{p.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{p.subtitle}</p>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <button onClick={() => openSearch(null, p.prefer, p.title, { concept: p.concept, role: p.role, promptId: p.id, presetQuery: p.concept })} className="inline-flex items-center gap-1 text-xs font-medium bg-secondary text-foreground border border-border/40 rounded-full px-3 py-1.5 hover:bg-secondary/70" data-testid={'add-visible-search-' + p.id}><Search size={12} />Search to add</button>
                  <button onClick={() => setPrompts((ps) => ps.filter((x) => x.id !== p.id))} className="text-xs font-medium text-muted-foreground px-3 py-1.5 ml-auto" data-testid={'add-visible-skip-' + p.id}>Skip</button>
                </div>
              </div>
            ))}

            {pendingAdd && (
              <div className="bg-card border border-border/40 rounded-xl px-3.5 py-3 flex items-center gap-2" role="status" aria-live="polite" data-testid="pending-add-row">
                {pendingAdd.status === 'pending'
                  ? <Loader2 size={16} className="text-accent animate-spin shrink-0" data-testid="pending-add-spinner" />
                  : <AlertTriangle size={16} className="text-amber-600 shrink-0" />}
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-foreground truncate" data-testid="pending-add-name">{cleanLabel(pendingAdd.food.record)}</span>
                  <span className="block text-xs text-muted-foreground" data-testid="pending-add-status">
                    {pendingAdd.status === 'pending' ? 'Adding to your meal…' : 'Couldn’t add this food.'}
                  </span>
                </span>
                {pendingAdd.status === 'error' && (
                  <>
                    <button onClick={() => addRebuiltFood(pendingAdd.food)} className="text-xs font-semibold text-accent px-1.5 py-1" data-testid="pending-add-retry">Try again</button>
                    <button onClick={() => setPendingAdd(null)} className="text-xs font-medium text-muted-foreground px-1.5 py-1" data-testid="pending-add-dismiss">Dismiss</button>
                  </>
                )}
              </div>
            )}
            <button onClick={() => openSearch(null, null, 'Add food')} disabled={Boolean(pendingAdd && pendingAdd.status === 'pending')} className="w-full bg-card border border-border/40 rounded-xl px-3.5 py-3 flex items-center gap-2 text-left hover:border-accent/50 disabled:opacity-60" data-testid="add-item-btn">
              <Plus size={16} className="text-accent" />
              <span className="text-sm font-semibold text-foreground">Add anything missing?</span>
              <Search size={14} className="text-muted-foreground ml-auto" />
            </button>
          </div>
        </section>

        <p className="text-[11px] text-muted-foreground text-center pt-1" data-testid="ai-estimate-disclosure">AI nutrition estimates may be inaccurate. Review ingredients, portions, and nutrition before logging.</p>
      </div>

      {/* Bottom CTA */}
      <div className="p-4 bg-background border-t border-border/40 space-y-2" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}>
        {logState && typeof logState === 'object' ? (
          logState.committed ? (
            <div className="rounded-xl bg-accent/10 border border-accent/30 p-3.5 text-center" data-testid="log-success">
              <p className="text-sm font-semibold text-foreground" data-testid="log-success-title">Logged ✓</p>
              <p className="text-xs text-muted-foreground mt-1" data-testid="log-success-summary">
                Logged <span className="font-semibold text-foreground">{logState.logged_count}</span> item{logState.logged_count === 1 ? '' : 's'} · {logState.totals?.calories} cal
                {(logState.excluded?.length || 0) > 0 && <> · {logState.excluded.length} not logged</>}
              </p>
            </div>
          ) : (
            <div className="rounded-xl bg-card border border-border/40 p-3.5 text-center" data-testid="log-success">
              <p className="text-sm font-semibold text-foreground">
                {logState.commit_enabled ? 'Logged' : 'Preview — logging is off'}
              </p>
              <p className="text-xs text-muted-foreground mt-1" data-testid="log-success-summary">
                Would log <span className="font-semibold text-foreground">{logState.would_write_count}</span> item{logState.would_write_count === 1 ? '' : 's'} · {logState.totals?.calories} cal
                {(logState.excluded?.length || 0) > 0 && <> · {logState.excluded.length} not logged</>}
              </p>
              <button onClick={() => setLogState(null)} className="mt-3 text-xs font-medium text-accent" data-testid="log-success-back">Back to review</button>
            </div>
          )
        ) : (
          <>
            <div className="flex items-center gap-2" data-testid="meal-date-selector">
              <span className="text-xs font-medium text-muted-foreground shrink-0 w-10">Date</span>
              <label className="flex-1 flex items-center gap-2 h-9 px-3 rounded-lg border border-border/40 bg-card text-foreground hover:border-accent/50 transition-colors cursor-pointer">
                <Calendar size={14} className="text-muted-foreground shrink-0" />
                <input type="date" value={effectiveDate} onChange={(e) => setMealDate(e.target.value || null)}
                  className="flex-1 bg-transparent text-xs font-medium outline-none" data-testid="meal-date-input" />
              </label>
            </div>
            <div className="flex items-center gap-2" data-testid="meal-slot-selector">
              <span className="text-xs font-medium text-muted-foreground shrink-0 w-10">Log to</span>
              <div className="flex-1 grid grid-cols-4 gap-1.5">
                {['Breakfast', 'Lunch', 'Dinner', 'Snack'].map((slot) => (
                  <button key={slot} onClick={() => setMealSlot(slot)}
                    className={'h-9 rounded-lg text-xs font-medium border transition-colors ' + (mealSlot === slot ? 'bg-accent text-white border-accent' : 'bg-card text-foreground border-border/40 hover:border-accent/50')}
                    data-testid={'meal-slot-' + slot.toLowerCase()}>{slot}</button>
                ))}
              </div>
            </div>
            <button onClick={doCommit} disabled={committing || committedDone || needsSelection || RR.hasBlockingUnresolved(unresolved) || Boolean(pendingAdd && pendingAdd.status === 'pending')} className="w-full h-12 rounded-xl bg-accent text-white font-semibold shadow-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="confirm-log-btn"><Check size={18} />{needsSelection ? 'Select a food to log' : (RR.hasBlockingUnresolved(unresolved) ? 'Set amounts to log' : ('Log meal · ' + subtotal + ' cal'))}</button>
            <p className="text-[11px] text-muted-foreground text-center" data-testid="commit-mode-note">{reviewCommitNote(commitAvailable, rebuildCommitAvailable)}</p>
          </>
        )}
      </div>

      {/* Portion / swap sheet */}
      {sheetRow && (
        <div className="fixed inset-0 z-50 flex items-end" style={kb ? { bottom: kb } : undefined} data-testid="portion-sheet">
          <div className="absolute inset-0 bg-black/40" onClick={() => { if (RR.canClosePortionSheet(savingPortion)) closeSheet(); }} data-testid="sheet-backdrop" />
          <div className="relative w-full bg-background rounded-t-2xl p-4 max-h-[75vh] overflow-y-auto shadow-xl" data-testid="portion-panel"
            style={kb
              ? { maxHeight: 'calc(100% - env(safe-area-inset-top) - 0.5rem)', paddingBottom: '1rem' }
              : { paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}>
            <div className="w-10 h-1 bg-border rounded-full mx-auto mb-3" />
            <p className="text-sm font-semibold text-foreground" data-testid={'sheet-name-' + sheetRow.id}>{(sheetRow.source_path === 'structured_component' && sheetRow.concept) ? cleanLabel(sheetRow.concept) : (sheetRow.component === 'dressings_or_sauce' ? cleanLabel(sauceLabelOf(sheetRow)) : (sheetRow.display_label || cleanLabel(sheetRow.record)))}</p>
            {sheetRow.substitution_of && (
              <span className="inline-block text-[11px] text-muted-foreground mt-0.5" data-testid={'sheet-substitution-' + sheetRow.id}>Replacing {cleanLabel(sheetRow.substitution_of)}</span>
            )}
            {(() => {
              // "Based on:" shows only a GENUINELY different supporting record. rebuild-v2 sets
              // display_label/record = semantic_identity, so this is hidden (no "rice / Based on: rice").
              const nm = (sheetRow.source_path === 'structured_component' && sheetRow.concept) ? cleanLabel(sheetRow.concept) : (sheetRow.component === 'dressings_or_sauce' ? cleanLabel(sauceLabelOf(sheetRow)) : (sheetRow.display_label || cleanLabel(sheetRow.record)));
              const sup = supportRecordFor(nm, sheetRow.record);
              return sup ? (<p className="text-xs text-muted-foreground mt-0.5" data-testid={'sheet-based-on-' + sheetRow.id}>Based on: {sup}</p>) : null;
            })()}
            {sheet.mode === 'swap' ? (
              <div className="mt-3 space-y-1.5">
                <p className="text-xs text-muted-foreground mb-1">Pick a different match</p>
                {sheetRow.candidates.map((c) => (
                  <button key={c.record} onClick={() => confirmCandidate(sheetRow.id, c)} className="w-full flex items-center justify-between gap-2 bg-card border border-border/40 rounded-xl px-3 py-2.5 hover:border-accent text-left" data-testid={'row-candidate-' + sheetRow.id + '-' + c.record.replace(/[^a-z0-9]/gi, '')}>
                    <span className="text-sm text-foreground truncate">{cleanLabel(c.record)}<span className="text-xs text-muted-foreground"> · {c.amount} {cleanLabel(c.unit)}</span></span>
                    <span className="text-sm font-medium text-foreground shrink-0">{Math.round(c.perUnit.calories * c.amount)} cal</span>
                  </button>
                ))}
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm text-muted-foreground">Amount</span>
                  <div className="flex items-center gap-3">
                    <button disabled={sheetRebuilt && savingPortion} onClick={sheetRebuilt
                        ? () => setPortionDraft((d) => RR.adjustPortionDraft(d, -0.25))
                        : () => editPortion(sheetRow.id, { amount: Math.max(0.25, r1(sheetRow.amount - 0.25)) })}
                      className="size-9 rounded-full bg-card border border-border/40 flex items-center justify-center disabled:opacity-40" data-testid={'row-amt-minus-' + sheetRow.id}><Minus size={16} /></button>
                    <input type="number" inputMode="decimal" step="0.25" min="0.25"
                      disabled={sheetRebuilt && savingPortion}
                      value={sheetRebuilt ? (portionDraft ? portionDraft.text : '') : sheetRow.amount}
                      onChange={sheetRebuilt
                        ? (e) => setPortionDraft((d) => RR.setPortionDraftText(d, e.target.value))
                        : (e) => { const v = parseFloat(e.target.value); if (!isNaN(v) && v > 0) editPortion(sheetRow.id, { amount: r1(v) }); }}
                      onFocus={(e) => e.target.select()}
                      className="font-mono text-lg font-bold text-foreground w-16 text-center bg-card border border-border/40 rounded-lg py-1 outline-none focus:border-accent disabled:opacity-40" data-testid={'row-amt-input-' + sheetRow.id} />
                    {sheetRebuilt && portionDraft && portionDraft.unit && (
                      <span className="text-sm text-muted-foreground" data-testid={'row-amt-unit-' + sheetRow.id}>{pluralUnit(portionDraft.unit, Number(portionDraft.value))}</span>
                    )}
                    <button disabled={sheetRebuilt && savingPortion} onClick={sheetRebuilt
                        ? () => setPortionDraft((d) => RR.adjustPortionDraft(d, 0.25))
                        : () => editPortion(sheetRow.id, { amount: r1(sheetRow.amount + 0.25) })}
                      className="size-9 rounded-full bg-card border border-border/40 flex items-center justify-center disabled:opacity-40" data-testid={'row-amt-plus-' + sheetRow.id}><Plus size={16} /></button>
                  </div>
                </div>
                {sheetServingVisible && (
                  <div className="mt-4 space-y-1.5">
                    <p className="text-xs text-muted-foreground">Serving</p>
                    {sheetServingOptions.map((o, oi) => {
                      // Rebuilt rows: selection is marked by the TRUSTED provider option's unit+size (dedup
                      // is by unit, so this is unambiguous); legacy rows keep matching on unit.
                      const selected = sheetRebuilt
                        ? (portionDraft && portionDraft.unit === o.unit && Number(portionDraft.value) === Number(o.size))
                        : (o.unit === sheetRow.unit);
                      return (
                        <button key={(o.serving_id != null ? o.serving_id : o.unit) + '-' + oi} disabled={sheetRebuilt && savingPortion} onClick={sheetRebuilt
                            ? () => setPortionDraft((d) => RR.applyPortionServingOption(d, o))
                            : () => editPortion(sheetRow.id, { unit: o.unit, amount: o.size, perUnit: o.perUnit })}
                          className={'w-full flex items-center justify-between gap-2 rounded-xl px-3 py-2.5 border disabled:opacity-40 ' + (selected ? 'border-accent bg-accent/5' : 'border-border/40 bg-card')} data-testid={'row-unit-' + sheetRow.id + '-' + String(o.unit).replace(/[^a-z0-9]/gi, '')}>
                          <span className="text-sm text-foreground text-left">
                            {o.size != null && `${+o.size} `}{pluralUnit(o.unit, o.size)}
                            {o.description && String(o.description).toLowerCase() !== String(o.unit).toLowerCase() && (
                              <span className="text-[11px] text-muted-foreground"> · {o.description}</span>
                            )}
                          </span>
                          {selected && <Check size={15} className="text-accent shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="flex items-center gap-3 mt-5">
                  {!savingPortion && sheetRow.candidates && sheetRow.candidates.length > 1 && (<button onClick={() => setSheet({ id: sheetRow.id, mode: 'swap' })} className="inline-flex items-center gap-1 text-sm font-medium text-accent" data-testid={'sheet-swap-' + sheetRow.id}><RefreshCw size={14} />Swap food</button>)}
                  <button onClick={sheetRebuilt ? commitPortion : closeSheet} disabled={sheetRebuilt && !RR.canSubmitPortion(savingPortion, portionDraft)} className="ml-auto h-11 px-6 rounded-xl bg-accent text-white text-sm font-semibold disabled:opacity-60" data-testid="sheet-done">{sheetRebuilt && savingPortion ? 'Saving…' : 'Done'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Real FatSecret search sheet — reuses the normal Log Food search/detail endpoints */}
      {search && (
        <FoodSearchSheet title={search.title} presetQuery={search.presetQuery} prefer={search.prefer} onAdd={onSearchAdd} onClose={() => setSearch(null)} />
      )}
    </div>
  );
}
